import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Plus, FileText, ArrowRight, Trash2, Edit } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { useAlert } from '../../context/alert/AlertProvider';
import { useTranslation } from 'react-i18next';
import EditScenarioModal from '../../components/forms/scenario/EditScenarioModal';
import ConfirmDeleteModal from '../../components/forms/scenario/ConfirmDeleteModal';
import TutorialOverlay from '../../components/ui/TutorialOverlay';
import ExampleScenario from '../../components/forms/scenario/ExampleScenario';

const ScenarioSelectionPage = () => {
  const [scenarios, setScenarios] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingScenario, setEditingScenario] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [scenarioToDelete, setScenarioToDelete] = useState(null);
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);

  const navigate = useNavigate();
  const location = useLocation();
  const { showAlert } = useAlert();
  const { t } = useTranslation();

  const isExampleMode = location.state?.isExample;

  const exampleScenarioRef = useRef(null);
  const createScenarioButtonRef = useRef(null);
  const selectScenarioButtonRef = useRef(null);

  const tutorialSteps = [
    {
      title: t("tutorial.scenario.title"),
      description: t("tutorial.scenario.description"),
      position: "center"
    },
    {
      title: t("tutorial.scenario.example.title"),
      description: t("tutorial.scenario.example.description"),
      position: "top-right",
      elementRef: exampleScenarioRef
    },
    {
      title: t("tutorial.scenario.create.title"),
      description: t("tutorial.scenario.create.description"),
      position: "center",
      elementRef: createScenarioButtonRef
    },
    {
      title: t("tutorial.scenario.select.title"),
      description: t("tutorial.scenario.select.description"),
      position: "center",
      elementRef: selectScenarioButtonRef
    }
  ];

  const fetchScenarios = useCallback(async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch('https://www.ia-schedule.com:4139/api/scenario', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch scenarios');
      }

      const data = await response.json();
      setScenarios(data);
    } catch (error) {
      console.error('Error fetching scenarios:', error);
      showAlert({
        title: t('alert.error'),
        description: t('alert.error.fetchScenarios'),
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  }, [showAlert, t]);

  useEffect(() => {
    if (isExampleMode) {
      setShowTutorial(true);
      setIsLoading(false);
    } else {
      fetchScenarios();
    }
  }, [isExampleMode, fetchScenarios]);

  const handleScenarioSelect = (scenario) => {
    sessionStorage.setItem('currentScenario', JSON.stringify(scenario));
    navigate('/form/collaborators', {
      state: {
        scenarioId: scenario.id,
        scenarioName: scenario.name,
        isExample: isExampleMode
      }
    });
  };

  const handleCreateScenario = async (scenarioData) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('https://www.ia-schedule.com:4139/api/scenario', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(scenarioData)
      });

      if (!response.ok) {
        throw new Error('Failed to create scenario');
      }

      const newScenario = await response.json();
      setScenarios([...scenarios, newScenario]);
      setShowCreateModal(false);

      showAlert({
        title: t('alert.success'),
        description: t('scenarios.created'),
        variant: 'success'
      });
    } catch (error) {
      console.error('Error creating scenario:', error);
      showAlert({
        title: t('alert.error'),
        description: t('alert.error.createScenario'),
        variant: 'destructive'
      });
    }
  };

  const handleEditScenario = (scenario) => {
    setEditingScenario(scenario);
    setShowCreateModal(true);
  };

  const handleUpdateScenario = async (scenarioData) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`https://www.ia-schedule.com:4139/api/scenario/${editingScenario.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(scenarioData)
      });

      if (!response.ok) {
        throw new Error('Failed to update scenario');
      }

      setScenarios(scenarios.map(s =>
        s.id === editingScenario.id
          ? { ...s, ...scenarioData }
          : s
      ));

      setShowCreateModal(false);
      setEditingScenario(null);

      showAlert({
        title: t('alert.success'),
        description: t('scenarios.updated'),
        variant: 'success'
      });
    } catch (error) {
      console.error('Error updating scenario:', error);
      showAlert({
        title: t('alert.error'),
        description: t('alert.error.updateScenario'),
        variant: 'destructive'
      });
    }
  };

  const handleDeleteClick = (scenario) => {
    setScenarioToDelete(scenario);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`https://www.ia-schedule.com:4139/api/scenario/${scenarioToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete scenario');
      }

      setScenarios(scenarios.filter(s => s.id !== scenarioToDelete.id));
      setShowDeleteModal(false);
      setScenarioToDelete(null);

      showAlert({
        title: t('alert.success'),
        description: t('scenarios.deleted'),
        variant: 'success'
      });
    } catch (error) {
      console.error('Error deleting scenario:', error);
      showAlert({
        title: t('alert.error'),
        description: t('alert.error.deleteScenario'),
        variant: 'destructive'
      });
    }
  };

  const handleNextTutorialStep = () => {
    if (tutorialStep < tutorialSteps.length - 1) {
      setTutorialStep(prev => prev + 1);
    } else {
      setShowTutorial(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 -mt-16 pt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('scenarios.title')}</h1>
            <p className="text-sm text-gray-500 mt-1">{t('scenarios.subtitle')}</p>
          </div>
          <button
            ref={createScenarioButtonRef}
            onClick={() => {
              if (!isExampleMode) {
                setEditingScenario(null);
                setShowCreateModal(true);
              }
            }}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 ${isExampleMode
              ? 'bg-gray-400 text-white cursor-not-allowed'
              : 'bg-indigo-600 text-white hover:bg-indigo-700'
              }`}
            disabled={isExampleMode}
          >
            <Plus size={18} />
            {t('scenarios.create')}
          </button>
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        ) : isExampleMode ? (
          <div className="flex mt-4">
            <ExampleScenario
              onSelect={handleScenarioSelect}
              exampleScenarioRef={exampleScenarioRef}
              selectButtonRef={selectScenarioButtonRef}
            />
          </div>
        ) : scenarios.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-medium text-gray-900 mb-2">{t('scenarios.empty.title')}</h3>
              <p className="text-gray-600 mb-6">{t('scenarios.empty.description')}</p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 inline-flex items-center gap-2"
              >
                <Plus size={18} />
                {t('scenarios.create')}
              </button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {scenarios.map(scenario => (
              <Card key={scenario.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4 sm:p-6">
                  <div className="flex flex-col h-full">
                    <div className="mb-4">
                      <div className="flex justify-between items-start">
                        <h3 className="text-lg font-semibold text-gray-900 mb-1 pr-8">{scenario.name}</h3>
                        <div className="flex gap-2 shrink-0">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditScenario(scenario);
                            }}
                            className="p-1 text-gray-400 hover:text-indigo-600 rounded-full hover:bg-gray-100"
                            aria-label="Edit"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(scenario);
                            }}
                            className="p-1 text-gray-400 hover:text-red-600 rounded-full hover:bg-gray-100"
                            aria-label="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>

                      {scenario.description && (
                        <p className="text-gray-600 text-sm mb-4 line-clamp-3">{scenario.description}</p>
                      )}

                    </div>

                    <div className="mt-auto">
                      <button
                        onClick={() => handleScenarioSelect(scenario)}
                        className="w-full mt-2 py-2 px-4 bg-indigo-100 text-indigo-700 hover:bg-indigo-200 rounded-lg flex items-center justify-center gap-2 transition-colors"
                      >
                        {t('scenarios.select')}
                        <ArrowRight size={16} />
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <EditScenarioModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setEditingScenario(null);
        }}
        onSave={editingScenario ? handleUpdateScenario : handleCreateScenario}
        scenario={editingScenario}
        translations={t}
      />

      <ConfirmDeleteModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title={t('scenarios.delete.title')}
        message={t('scenarios.delete.message', { name: scenarioToDelete?.name })}
      />

      <TutorialOverlay
        isVisible={showTutorial}
        onClose={() => setShowTutorial(false)}
        title={tutorialSteps[tutorialStep]?.title}
        description={tutorialSteps[tutorialStep]?.description}
        position={tutorialSteps[tutorialStep]?.position}
        targetRef={tutorialSteps[tutorialStep]?.elementRef}
        onNext={handleNextTutorialStep}
        isLastStep={tutorialStep === tutorialSteps.length - 1}
      />
    </div>
  );
};

export default ScenarioSelectionPage;